import { Pages } from 'modules/navigation/domain/enums/pages';

/**
 * Запрос AI-клиента (MCP) на доступ: бэкенд приводит человека на `/sign-in` и `/oauth-consent`
 * с параметрами своего `/oauth/authorize` в адресе.
 *
 * Параметры дальше передаются **строкой как пришли**, без разбора и сборки заново: бэкенд сверяет
 * `state` и `code_challenge` побайтно, а `URLSearchParams` перекодирует `+`, `%20` и порядок.
 */
export interface OauthAuthorizeRequest {
  /** Исходная строка запроса с `?` — её и отдаём бэкенду. */
  search: string;
  redirectUri: string;
  /** Хост `redirect_uri` — по нему человек узнаёт клиента: `client_id` у бэкенда — телефон пользователя. */
  redirectHost: string;
  state?: string;
}

/** Бэкенд ставит эти три в каждый редирект на вход и согласие; без них это не OAuth. */
function hasOauthMarkers(params: URLSearchParams): boolean {
  return params.get('response_type') === 'code' && !!params.get('client_id') && !!params.get('redirect_uri');
}

/** Адрес — OAuth-запрос: тогда вход ведёт не на главную, а на согласие. */
export function isOauthAuthorizeSearch(search: string): boolean {
  return hasOauthMarkers(new URLSearchParams(search));
}

/** Разбор для экрана согласия; `null` — параметры неполные или адрес возврата не `http(s)`. */
export function parseOauthAuthorizeRequest(search: string): OauthAuthorizeRequest | null {
  const params = new URLSearchParams(search);
  if (!hasOauthMarkers(params)) return null;

  const redirectUri = params.get('redirect_uri') as string;
  let url: URL;
  try {
    url = new URL(redirectUri);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;

  return {
    search: search.startsWith('?') ? search : `?${search}`,
    redirectUri,
    redirectHost: url.host,
    state: params.get('state') ?? undefined,
  };
}

/** Экран согласия с теми же параметрами. */
export function oauthConsentPath(search: string): string {
  return `${Pages.OAUTH_CONSENT}${search}`;
}

/**
 * Куда вести на вход: для согласия — `/sign-in` с теми же параметрами, чтобы они пережили и
 * перезагрузку, и переход на регистрацию; иначе — просто `/sign-in`.
 */
export function signInPathFor(continueFrom?: string): string {
  const prefix = `${Pages.OAUTH_CONSENT}?`;
  if (continueFrom?.startsWith(prefix) && isOauthAuthorizeSearch(continueFrom.slice(prefix.length - 1))) {
    return `${Pages.SIGN_IN}${continueFrom.slice(prefix.length - 1)}`;
  }
  return Pages.SIGN_IN;
}

/** Отказ по RFC 6749 §4.1.2.1: `error=access_denied` и исходный `state`. */
export function buildOauthDenyUrl(request: OauthAuthorizeRequest): string {
  const url = new URL(request.redirectUri);
  url.searchParams.set('error', 'access_denied');
  if (request.state !== undefined) url.searchParams.set('state', request.state);
  return url.toString();
}

/**
 * Адрес с кодом от бэкенда ведёт туда, куда просил клиент. Код уходит только на `redirect_uri`
 * этого запроса — иначе переход не делаем.
 */
export function isRedirectForRequest(redirectUrl: string, request: OauthAuthorizeRequest): boolean {
  if (!redirectUrl.startsWith(request.redirectUri)) return false;
  const rest = redirectUrl.slice(request.redirectUri.length);
  return rest === '' || rest.startsWith('?') || rest.startsWith('&');
}
